import * as Schema from "effect/Schema";
import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas.ts";
import { ProjectId } from "./baseSchemas.ts";

export const ProjectContributionAdditions = Schema.Struct({
  human: NonNegativeInt,
  agent: NonNegativeInt,
  unknown: NonNegativeInt,
});

export const ProjectContributionSummary = Schema.Struct({
  ideas: TrimmedNonEmptyString,
  decisions: TrimmedNonEmptyString,
  guidance: TrimmedNonEmptyString,
  execution: TrimmedNonEmptyString,
});
export type ProjectContributionSummary = typeof ProjectContributionSummary.Type;

/** Only aggregates cross the publication boundary; raw Agent Trace records stay local. */
export const ProjectContributionSnapshot = Schema.Struct({
  version: Schema.Literal("1"),
  cutoff: Schema.String.check(Schema.isPattern(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)),
  additions: ProjectContributionAdditions,
  humanPrompts: NonNegativeInt,
  timeline: Schema.Array(
    Schema.Struct({
      date: Schema.String.check(Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/)),
      human: NonNegativeInt,
      agent: NonNegativeInt,
      unknown: NonNegativeInt,
      humanPrompts: NonNegativeInt,
    }),
  ),
  summary: ProjectContributionSummary,
});
export type ProjectContributionSnapshot = typeof ProjectContributionSnapshot.Type;

export const PublicContributionProjectInput = Schema.Struct({
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(160)),
  publisherName: TrimmedNonEmptyString.check(Schema.isMaxLength(160)),
  repositoryUrl: Schema.NullOr(
    Schema.String.check(
      Schema.isPattern(/^https:\/\/github\.com\/[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+\/?$/),
    ),
  ),
});
export type PublicContributionProjectInput = typeof PublicContributionProjectInput.Type;

export const CreatePublicContributionProjectInput = Schema.Struct({
  ...PublicContributionProjectInput.fields,
  creationId: TrimmedNonEmptyString.check(Schema.isMaxLength(160)),
});
export type CreatePublicContributionProjectInput = typeof CreatePublicContributionProjectInput.Type;

export const PublicContributionProject = Schema.Struct({
  id: TrimmedNonEmptyString,
  publicUrl: Schema.String,
  ...PublicContributionProjectInput.fields,
});
export type PublicContributionProject = typeof PublicContributionProject.Type;

export const PublishedProjectContribution = Schema.Struct({
  id: TrimmedNonEmptyString,
  publishedAt: NonNegativeInt,
  snapshot: ProjectContributionSnapshot,
});
export type PublishedProjectContribution = typeof PublishedProjectContribution.Type;

export const PublicContributionProjectPage = Schema.Struct({
  project: PublicContributionProject,
  latest: Schema.NullOr(PublishedProjectContribution),
  snapshots: Schema.Array(PublishedProjectContribution),
});
export type PublicContributionProjectPage = typeof PublicContributionProjectPage.Type;

export const ProjectContributionGenerationPhase = Schema.Literals([
  "generating",
  "ready",
  "failed",
  "cancelled",
]);
export type ProjectContributionGenerationPhase = typeof ProjectContributionGenerationPhase.Type;

export const ProjectContributionGeneration = Schema.Struct({
  revision: NonNegativeInt,
  id: TrimmedNonEmptyString,
  projectId: ProjectId,
  branch: TrimmedNonEmptyString,
  phase: ProjectContributionGenerationPhase,
  createdAt: Schema.String,
  inspectedPages: NonNegativeInt,
  requiredPages: NonNegativeInt,
  failure: Schema.NullOr(Schema.String),
  snapshot: Schema.NullOr(ProjectContributionSnapshot),
});
export type ProjectContributionGeneration = typeof ProjectContributionGeneration.Type;

export const ProjectContributionGenerationInput = Schema.Struct({
  id: TrimmedNonEmptyString,
  projectId: ProjectId,
  branch: TrimmedNonEmptyString,
});
export type ProjectContributionGenerationInput = typeof ProjectContributionGenerationInput.Type;

export class ProjectContributionGenerationError extends Schema.TaggedErrorClass<ProjectContributionGenerationError>()(
  "ProjectContributionGenerationError",
  {
    message: Schema.String,
  },
) {}
